import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SEOSchema from '@/components/feature/SEOSchema';
import { articles } from '@/mocks/articles';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-4">文章未找到</h1>
            <Link
              to="/articles"
              className="text-primary-600 hover:text-primary-700 font-medium cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              返回文章列表
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const contentHtml = article.content
    .replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold text-foreground-900 mt-6 mb-3">$1</h3>')
    .replace(/^## (.+)$/gm, '<h2 class="text-xl font-heading text-foreground-950 mt-8 mb-4">$1</h2>')
    .replace(/^\|(.+)\|$/gm, (match) => {
      const cells = match.split('|').filter(c => c.trim());
      const isHeader = match.includes('---');
      if (isHeader) return '';
      const isHeaderRow = cells.some(c => c.trim().match(/^-+$/));
      if (isHeaderRow) return '';
      const cellTag = match.includes('---') ? 'th' : 'td';
      return `<tr>${cells.map(c => `<${cellTag} class="border border-background-300 px-3 py-2 text-sm">${c.trim()}</${cellTag}>`).join('')}</tr>`;
    })
    .replace(/^(\d+)\. (.+)$/gm, '<li class="ml-5 list-decimal text-sm text-foreground-700 leading-relaxed mb-1">$2</li>')
    .replace(/^- \*\*(.+)\*\*：(.+)$/gm, '<li class="ml-5 list-disc text-sm text-foreground-700 leading-relaxed mb-1"><strong>$1</strong>：$2</li>')
    .replace(/^- (.+)$/gm, '<li class="ml-5 list-disc text-sm text-foreground-700 leading-relaxed mb-1">$1</li>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-foreground-900">$1</strong>')
    .replace(/\n\n/g, '</p><p class="text-sm text-foreground-700 leading-relaxed mb-4">')
    .replace(/\n/g, '<br/>');

  const wrappedHtml = `<p class="text-sm text-foreground-700 leading-relaxed mb-4">${contentHtml}</p>`;

  return (
    <>
      <SEOHead
        title={`${article.title} | 麻将游戏在线玩`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://mahjong-online.com/articles/${article.id}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogUrl={`https://mahjong-online.com/articles/${article.id}`}
        ogType="article"
        articlePublishedTime={article.date}
        articleAuthor={article.author}
      />
      <SEOSchema
        type="Article"
        data={{
          headline: article.title,
          description: article.excerpt,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.date,
          image: article.image,
          url: `https://mahjong-online.com/articles/${article.id}`,
        }}
      />

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: '麻将攻略', path: '/articles' },
                { name: article.title },
              ]}
            />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xs px-2.5 py-1 bg-accent-100 text-accent-800 rounded-full whitespace-nowrap">
                  {article.categoryName}
                </span>
                <span className="text-xs text-foreground-400">{article.readTime} 阅读</span>
              </div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i> {article.date}
                </span>
              </div>
            </div>

            <div className="rounded-lg overflow-hidden mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-64 md:h-80 object-cover object-top"
              />
            </div>

            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: wrappedHtml }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 bg-background-100 rounded-lg">
                <span className="text-sm text-foreground-600">
                  觉得有用？分享给朋友一起学习麻将技巧！
                </span>
                <div className="flex items-center gap-2">
                  <button className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-wechat-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-weibo-line text-sm"></i>
                  </button>
                  <button className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer">
                    <i className="ri-link text-sm"></i>
                  </button>
                </div>
              </div>

              <div className="mt-8 text-center">
                <Link
                  to="/articles"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i> 返回文章列表
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}